import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { deleteTable, fetchTables } from "../../../../../api/application/table";
import type { APITable } from "../../../../../api/infrastructure/table.formatter";
import { useGetDatabaseCreationInfo } from "../../../../../store/store-hooks";
import { getUniqueId } from "../../../../shared/generate-unique-id";
import { showToast } from "../../../../shared/global-tools/toast-system";
import Icon from "../../../utils/Icons/Icon";
import NoDataMessage from "../../../utils/NoDataMessage/NoDataMessage";
import Spinner from "../../../utils/Spinner/Spinner";
import type { CreateDatabaseStepProps } from "../CreateDatabaseStepOne/CreateDatabaseStepOne";
import "./CreateDatabaseStepTwo.scss";
import TableCard from "./TableCard/TableCard";

export default function CreateDatabaseStepTwo({ navigateToStep }: CreateDatabaseStepProps) {
  const { getDatabaseCreationInfo } = useGetDatabaseCreationInfo();
  const databaseCreationInfo = getDatabaseCreationInfo();
  const [tables, setTables] = useState<APITable[]>([
    {
      id: `temp_${getUniqueId()}`,
      label: "",
      name: "",
      description: "",
      columns: [],
    },
  ]);
  const firstTable = tables[0];
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);

  const addDatabaseTable = () => {
    if (firstTable && (firstTable.name === "" || firstTable.columns.length === 0)) {
      showToast("Le nom de la table et les champs sont requis", {
        severity: "warn",
      });
      return;
    }
    if (tables.some((table) => table.name === firstTable.name && table.id !== firstTable.id)) {
      showToast("Le nom de la table est déjà utilisé", {
        severity: "warn",
      });
      return;
    }
    tables.unshift({
      id: `temp_${getUniqueId()}`,
      label: "",
      name: "",
      description: "",
      columns: [],
    });
    setTables([...tables]);
  };

  const performTableDeletion = async (tableId: string) => {
    if (!databaseCreationInfo) return;
    if (tableId.includes("temp_")) {
      const updatedTables = tables.filter((t) => t.id !== tableId);
      setTables(updatedTables);
      return;
    }
    const tableIdToDelete = Number(tableId);
    if (Number.isNaN(tableIdToDelete)) {
      showToast("table id is invalid", { severity: "warn" });
      return;
    }
    await deleteTable(databaseCreationInfo.referenceId, tableIdToDelete);
  };

  const loadTables = wrapAsync(async () => {
    if (!databaseCreationInfo) return;
    try {
      setLoading(true);
      const res = await fetchTables(databaseCreationInfo.referenceId);
      if (res.length === 0) return;
      setTables([...res]);
    } catch (error) {
      console.error("Error fetching reference:", error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (!databaseCreationInfo) {
      navigateToStep(1);
      return;
    }
    loadTables();
  }, []);

  if (!databaseCreationInfo) return;
  if (loading) return <Spinner />;
  return (
    <div className="CreateDatabaseStepTwo">
      <div className="CreateDatabaseStepTwo-header">
        <h2 className="CreateDatabaseStepTwo-title">
          Configuration générale du référentiel
        </h2>

        <Button
          className="Btn success sm"
          label="Ajouter une table"
          onClick={addDatabaseTable}
          icon={<Icon name="plus" />}
          disabled={firstTable && (firstTable.name === "" || firstTable.columns.length === 0)}
        />
      </div>

      {tables.length > 0 ? (
        <div className="CreateDatabaseStepTwo-tableList">
          {tables.map((table, index) => (
            <TableCard
              key={table.id}
              tableNumber={tables.length - index}
              initCollapse={index !== 0}
              referenceId={databaseCreationInfo.referenceId}
              currentTable={table}
              tables={tables}
              onRemoveTable={performTableDeletion}
              refreshTableList={loadTables}
            />
          ))}
        </div>
      ) : (
        <NoDataMessage message="Aucune table pour le moment" />
      )}

      <div className="CreateDatabaseStepTwo-footer">
        <Button
          className="Btn secondary outlined"
          label="Retour"
          onClick={() => navigateToStep(1)}
        />
        <Button
          className="Btn primary"
          label="Continuer"
          onClick={() => navigateToStep(3)}
          disabled={firstTable && (firstTable.name === "" || firstTable.columns.length === 0)}
        />
      </div>
    </div>
  );
}
